import { AnalyticsEventName } from '@dupe/common/constants'
import {
  PhoneAuthProvider,
  RecaptchaVerifier,
  getAuth,
  signInWithCredential,
} from 'firebase/auth'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { InputOTP } from '~/src/components/ui/input-otp'
import { InputOTPGroup, InputOTPSlot } from '~/src/components/ui/input-otp'
import { useAuthContext } from '~/src/context/AuthProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import { AuthUserWithToken } from '~/src/hooks/useAuthUser'
import app from '~/src/lib/firebase/firebaseApp'

interface Props {
  phoneNumber: string
  verificationId: string
  recaptchaVerifier: RecaptchaVerifier | undefined
  setProcessing: (processing: boolean) => void
  setErrorMessage: (errorMessage: string | null) => void
  onClick: () => void
  callback: () => void
}

const auth = getAuth(app)

const PhoneNumberVerification: React.FC<Props> = ({
  phoneNumber,
  verificationId,
  recaptchaVerifier,
  setProcessing,
  setErrorMessage,
  onClick,
  callback,
}) => {
  const analytics = useAnalytics()
  const { authSource } = useAuthContext()

  const handleVerifyCode = async (code: string) => {
    setErrorMessage(null)
    setProcessing(true)

    if (!code) {
      setErrorMessage('Please enter a code')
      setProcessing(false)
      return
    }

    const credential = PhoneAuthProvider.credential(verificationId, code)

    // check if error with credential
    if (!credential) {
      setProcessing(false)
      setErrorMessage('Error verifying code')

      analytics.track(AnalyticsEventName.AuthModalVerifyCodeError, {
        verificationId,
        verificationCode: code,
      })

      return
    }

    try {
      const resp = (await signInWithCredential(auth, credential)) as {
        user: AuthUserWithToken
      }

      // remove recaptcha
      recaptchaVerifier?.clear()

      if (resp.user) {
        analytics.track(AnalyticsEventName.UserSignedIn, {
          method: 'phone',
          source: authSource,
          userId: resp.user.uid,
        })

        setProcessing(false)
        callback()
      } else {
        setProcessing(false)
        setErrorMessage('There was an error creating your account')
      }
    } catch (error: any) {
      setProcessing(false)
      switch (error?.code) {
        case 'auth/credential-already-in-use':
        case 'auth/account-exists-with-different-credential':
          setErrorMessage('Phone number already linked to another account')
          break
        case 'auth/invalid-verification-code':
          setErrorMessage('Invalid code')
          break
        case 'auth/code-expired':
          setErrorMessage('Code expired')
          break
        default:
          setErrorMessage('Error verifying code')
      }

      analytics.track(AnalyticsEventName.AuthModalVerifyCodeError, {
        verificationId,
        verificationCode: code,
        error,
      })
    }
  }

  return (
    <div className='flex flex-col items-center justify-center space-y-10'>
      <InputOTP
        autoFocus
        maxLength={6}
        pattern={REGEXP_ONLY_DIGITS}
        onChange={(value) => {
          if (value.length === 6) {
            handleVerifyCode(value)
          }
        }}
      >
        <InputOTPGroup>
          <InputOTPSlot index={0} />
          <InputOTPSlot index={1} />
          <InputOTPSlot index={2} />
          <InputOTPSlot index={3} />
          <InputOTPSlot index={4} />
          <InputOTPSlot index={5} />
        </InputOTPGroup>
      </InputOTP>
      {!!phoneNumber && (
        <p className='text-center'>
          Code sent to <span className='font-mono'>{phoneNumber}</span> <br />
          <button onClick={onClick}>
            Wrong phone number? <span className='underline'>Change</span>
          </button>
        </p>
      )}
    </div>
  )
}

export default PhoneNumberVerification
